import {
  Box,
  Button,
  Dialog,
  DialogContentText,
  ListItem,
  MenuItem,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "../../../explorer/hooks/useTranslation";
import { AppIconsClose, AppIconsSearch } from "../../components/AppIcons";
import {
  AppModalDialog,
  AppModalTitle,
  DialogContentResults,
  DialogContentStyled,
  IconButtonClose,
  InputLabelStyled,
  TextFieldStyled,
} from "./BasketPage.styles";
import AppIconButtonPrimary from "../../components/AppButtons/AppIconButtonPrimary";
import { useClasses } from "./BasketPage.styles";
import ServiceProvider from "../../configuration/ServiceProvider";
import { useApiError } from "../../hook/useApiError";
import SkeletonList from "../SpareParts/HomePage/SkeletonList/SkeletonList";
import {
  BasketGetPartsByRef,
  BasketPartByRef,
} from "../../../explorer/types/Basket/BasketPartsByRef";
import useBrands from "../../../explorer/hooks/useBrands";
import useLanguages from "../../../explorer/hooks/useLanguages";

type TBasketAddPartByRef = {
  callBack: (
    code: string,
    replaceCode:string,
    manufacturerCompanyCode: string,
    machine: string,
    tableCode: string,
    position: string
  ) => void;
};

const BasketAddPartByRef: FC<TBasketAddPartByRef> = (props) => {
  const { callBack } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [machine, setMachine] = useState("");
  const [tableCode, setTableCode] = useState("");
  const [position, setPosition] = useState("");
  const [parts, setParts] = useState<BasketPartByRef[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const brand = useBrands().currentBrandUrl;
  const lang = useLanguages().currentLanguage;
  const [error, setError] = useApiError();

  const handleOpen = () => {
    setOpen(true);
    resetFilter();
  };

  const handleClose = () => {
    resetFilter();
    setOpen(false);
    setLoading(false);
  };

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(event.target.value.trim());
    };

  /**
   * Regex
   * @param {React.KeyboardEvent<HTMLDivElement>} event
   * @returns
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const re = /[a-zA-Z0-9\-\.\_]+/;
    if (!re.test(event.key)) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    // Ottieni il contenuto incollato dagli appunti
    const re = /^[a-zA-Z0-9\-._]+$/;

    // Se il contenuto non rispetta la regex, impedisci l'incollatura
    if (!re.test(event.clipboardData.getData("text"))) {
      event.preventDefault();
    }
  };

  const handleAddPartToBasket = (
    code: string,
    replacedCode:string,
    manufacturerCompanyCode: string,
    
  ) => {
    callBack(code, replacedCode, manufacturerCompanyCode, machine, tableCode, position);
    handleClose();
  };

  useEffect(() => {
    resetFilter();
    setParts([]);
  }, []);

  const searchParts = () => {
    setLoading(true);
    const rq: BasketGetPartsByRef = {
      machineSN: machine,
      manufacturerCompany: "SAVIO",
      tableCode: tableCode,
      position: position,
    };
    ServiceProvider.Basket.getPartByRef(brand, rq, lang)
      .then((response: BasketPartByRef[]) => {
        const helpPart: BasketPartByRef = {
          partCode: "13999.9999.0.0",
          description: "",
          manufacturerCompanyCode: "SAVIO",
          text: t("common.basket.INeedHelp"),
        };

        // Modifica la response per aggiornare il campo `text`
        const updatedResponse = response.map((part) => ({
          ...part,
          text: `${part.partCode} ${
            part.description !== "" ? `/ ${part.description}` : ""
          }`,
        }));

        // Aggiungi helpPart alla lista
        const partsList = [...updatedResponse, helpPart];

        setParts(partsList);
        setLoading(false);
      })
      .catch((error) => {
        setError(error?.data?.title);
        console.error(error);
        setLoading(false);
      });
  };

  const resetFilter = () => {
    setMachine("");
    setTableCode("");
    setPosition("");
    setParts([]);
  };

  const classes = useClasses();
  
  return (
    <Box sx={classes.noPrint}>
      <Button variant="contained" onClick={handleOpen}>
        {t("common.basket.AddPartByRef")}
      </Button>
      <AppModalDialog open={open} onClose={handleClose}>
        <AppModalTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            minWidth: 750,
          }}
          id="customized-dialog-title"
        >
          {t("common.basket.AddPartByRef")}
        </AppModalTitle>
        <IconButtonClose
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.primary.contrastText,
          })}
        >
          <AppIconsClose />
        </IconButtonClose>

        <DialogContentStyled>
          <Box
            sx={{
              flexGrow: 1,
              flexDirection: "row",
              gap: "14px",
              display: "flex",
              padding: '20px 20px 0',
            }}
          >
            <TextFieldStyled
              id="machine-textField"
              label={t("common.basket.machineSerialNumber")}
              variant="outlined"
              required
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onChange={handleInputChange(setMachine)}
              sx={{ width: "60%" }}
            />
            <TextFieldStyled
              id="tabelCode-textField"
              label={t("common.basket.illustrationCode")}
              variant="outlined"
              required
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onChange={handleInputChange(setTableCode)}
              sx={{ width: "50%" }}
            />
            <TextFieldStyled
              id="postion-textField"
              label={t("basket.position")}
              variant="outlined"
              required
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onChange={handleInputChange(setPosition)}
              sx={{ width: "30%" }}
            />

            <AppIconButtonPrimary
              sx={{ mt: "2px" }}
              disabled={!machine || !tableCode || !position}
              onClick={searchParts}
            >
              <AppIconsSearch />
            </AppIconButtonPrimary>
          </Box>

          <InputLabelStyled required>{t("common.required")}</InputLabelStyled>
          <DialogContentResults sx={classes.listItem}>
            {loading ? (
              <SkeletonList mkey={"partsList"} />
            ) : (
              parts.length > 0 &&
              parts.map((part: BasketPartByRef, idx: number) => (
                <ListItem key={idx}
                    sx={{
                      width: "100%",
                      justifyContent: "left",
                    }}
                    onClick={() =>{
                      let isReplaced =
                          part.replacementCode !== undefined &&
                          part.replacementCode !== null &&
                          part.replacementCode !== "";
                      handleAddPartToBasket(
                          isReplaced ? part.replacementCode!! : part.partCode,
                          isReplaced ? part.partCode : "",
                          part.manufacturerCompanyCode
                      );
                      }
                    }
                  >{`${part.text}`}
                </ListItem>
              ))
            )}
          </DialogContentResults>
        </DialogContentStyled>
      </AppModalDialog>
    </Box>
  );
};

export default BasketAddPartByRef;
